import refreshPermissionsClaim from '@/http/Helpers/refreshPermissionsClaim';
import * as Sentry from '@sentry/browser';

const { logger } = Sentry;

/**
 * A 403 from a `[RequirePermission]` endpoint, after the claim re-mint has been attempted.
 *
 * `claimWasRefreshed` tells the UI which of the two true statements to make:
 *   false — the token could not be re-minted silently; signing out and in will fix it.
 *   true  — the claim is freshly minted and STILL lacks the permission; it is not granted.
 * Callers must never render "please try again" for either: retrying is the one action that
 * cannot work, and that message is what turned ADO #7699 into a production ticket.
 */
export class PermissionDeniedError extends Error {
  readonly status = 403;
  readonly api: string;
  readonly claimWasRefreshed: boolean;
  /** The server's own 403 body, kept for diagnostics — not for display. */
  readonly serverMessage: string;

  constructor(api: string, claimWasRefreshed: boolean, serverMessage: string) {
    super(
      claimWasRefreshed
        ? 'You do not have permission to perform this action.'
        : 'Your access has changed. Sign out and sign in again, then try again.'
    );
    this.name = 'PermissionDeniedError';
    this.api = api;
    this.claimWasRefreshed = claimWasRefreshed;
    this.serverMessage = serverMessage;
  }
}

type PermissionDeniedListener = (error: PermissionDeniedError) => void;

const listeners = new Set<PermissionDeniedListener>();

/**
 * Subscribe to hard permission denials (a 403 that survived the claim re-mint). Used to refetch
 * the cached `GET /Permissions/User` list so a REVOKED permission stops being offered. It does
 * not hide the control in the #7699 direction — there the database really does grant the
 * permission, and the honest remedy is the re-authentication the error message asks for.
 * Returns an unsubscribe function.
 */
export const onPermissionDenied = (listener: PermissionDeniedListener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Outgoing request bodies, captured before the request is sent.
 *
 * A replay cannot recover the body on its own: by the time `onResponse` runs, `fetch` has
 * consumed the Request, so `request.clone()` throws and a naive retry would POST an empty
 * body — turning a healed 403 into an ASP.NET 400. Each client therefore registers its body
 * in `onRequest`, while the stream is still readable. Keyed weakly, so nothing is retained.
 */
const capturedBodies = new WeakMap<Request, ArrayBuffer>();

const carriesBody = (method: string) => method !== 'GET' && method !== 'HEAD';

/**
 * Record an outgoing request's body so a 403 replay can resend it. Call from every client's
 * `onRequest`, passing `body` when the client has already buffered it to rebuild the Request.
 */
export const rememberRequestBody = async (
  request: Request,
  body?: ArrayBuffer
): Promise<void> => {
  if (!carriesBody(request.method)) {
    return;
  }

  try {
    // Recorded even when empty: presence in the map means "captured", and a body-carrying
    // method with a legitimately empty body must not look like a capture failure.
    capturedBodies.set(request, body ?? (await request.clone().arrayBuffer()));
  } catch {
    // Unreadable body: the replay refuses rather than sending an empty one — see replay().
  }
};

/**
 * The outcome of a re-mint attempt.
 *
 * `claimIsFresh` carries the meaning a bare `null` token cannot: it says the claim HAS been
 * re-minted, so a denial carrying it is genuine rather than a stale-claim artefact.
 */
type RemintResult = { token: string | null; claimIsFresh: boolean };

/**
 * A row menu can fire several gated calls at once, and each would otherwise start its own B2C
 * journey. Single-flight collapses concurrent 403s onto one re-mint; the cooldown stops a
 * genuinely-unpermitted user re-minting on every click (retry storm, cascade type C-6).
 *
 * Both are keyed by SCOPE SET, not global. The eight wired clients do not share one audience —
 * `integrationGatewayApi` uses `VITE_SIGNEDIN_INTEGRATION_GATEWAY_ACCESS_SCOPE` while the other
 * seven use `VITE_SIGNEDIN_ACCESS_SCOPE` — so a global guard would hand one client a token
 * minted for the other's audience and replay with it, producing a 401 or a bogus
 * "you do not have permission".
 */
const REFRESH_COOLDOWN_MS = 30_000;

type ScopeState = {
  inFlight: Promise<RemintResult> | null;
  lastAttemptAt: number;
  lastResult: RemintResult;
};

const scopeStates = new Map<string, ScopeState>();

const scopeKey = (scopes: string[]) => [...scopes].sort().join(' ');

/** Test seam — resets the single-flight/cooldown state between cases. */
export const __resetStaleClaimRetryState = () => {
  scopeStates.clear();
};

const remintClaimOnce = (scopes: string[]): Promise<RemintResult> => {
  const key = scopeKey(scopes);
  const state = scopeStates.get(key) ?? {
    inFlight: null,
    lastAttemptAt: 0,
    lastResult: { token: null, claimIsFresh: false }
  };
  scopeStates.set(key, state);

  if (state.inFlight) {
    return state.inFlight;
  }

  if (
    state.lastAttemptAt !== 0 &&
    Date.now() - state.lastAttemptAt < REFRESH_COOLDOWN_MS
  ) {
    // The cooldown suppresses the RE-MINT, never the retry. A request that was already in
    // flight with the old token 403s after the refresh lands, and it deserves the same replay
    // its sibling got — denying it outright would reintroduce #7699's misdirection on the
    // recovery path. The token is at most REFRESH_COOLDOWN_MS old, far inside its lifetime.
    return Promise.resolve(state.lastResult);
  }

  state.lastAttemptAt = Date.now();
  state.inFlight = refreshPermissionsClaim(scopes)
    .then((token) => ({ token, claimIsFresh: Boolean(token) }))
    .catch((error: unknown) => {
      // A refresh that cannot happen silently is not an error the caller should see — the
      // original 403 is the real outcome, and swallowing it here keeps that true.
      logger.error('Could not re-mint the permissions claim after a 403', { error });
      return { token: null, claimIsFresh: false };
    })
    .then((result: RemintResult) => {
      state.lastResult = result;
      return result;
    })
    .finally(() => {
      state.inFlight = null;
    });

  return state.inFlight;
};

const readBody = async (response: Response): Promise<string> => {
  try {
    return await response.clone().text();
  } catch {
    return '';
  }
};

/**
 * Replay the original request with the re-minted token, preserving what the caller set —
 * notably `signal`, so React Query aborting an unmounted screen's request also cancels the
 * replay. Returns null when the body could not be recovered: sending a silently-empty one
 * would trade a 403 for a 400, which is worse than not replaying at all.
 */
const replay = async (request: Request, token: string): Promise<Response | null> => {
  const body = capturedBodies.get(request);
  if (carriesBody(request.method) && !capturedBodies.has(request)) {
    return null;
  }

  const headers = new Headers(request.headers);
  headers.set('Authorization', `Bearer ${token}`);

  const core: RequestInit = { method: request.method, headers };
  if (body && body.byteLength > 0) {
    core.body = body;
  }

  const full: RequestInit = {
    ...core,
    signal: request.signal,
    credentials: request.credentials,
    redirect: request.redirect,
    referrerPolicy: request.referrerPolicy,
    integrity: request.integrity
  };
  if (request.mode && request.mode !== 'navigate') {
    full.mode = request.mode;
  }

  let replayed: Request;
  try {
    replayed = new Request(request.url, full);
  } catch (error: unknown) {
    // Some runtimes reject a field copied off an existing Request (notably an AbortSignal
    // from another realm). Method, headers and body are what correctness depends on; the
    // rest is fidelity, so fall back rather than abandon the replay.
    logger.warn?.('Replaying a 403 without the original request options', { error });
    replayed = new Request(request.url, core);
  }

  return globalThis.fetch(replayed);
};

const asStatusError = async (response: Response) => {
  const error = new Error(response.statusText) as Error & { status: number };
  error.message = await readBody(response);
  error.status = response.status;
  return error;
};

const denied = (api: string, claimWasRefreshed: boolean, serverMessage: string) => {
  const error = new PermissionDeniedError(api, claimWasRefreshed, serverMessage);
  listeners.forEach((listener) => {
    try {
      listener(error);
    } catch (listenerError: unknown) {
      // A listener must never replace the error the caller is about to handle — that would
      // drop the call into a generic "something went wrong" branch.
      logger.error('A permission-denied listener threw', { error: listenerError });
    }
  });
  return error;
};

/**
 * Shared `onResponse` body for every B1Link OpenAPI client.
 *
 * Called at REQUEST time, never at module-evaluation time: the clients sit in an import cycle
 * with `root.tsx` (via the MSAL helpers), so a factory invoked while the module graph is still
 * settling resolves to `undefined`.
 *
 * Two jobs:
 *   1. Attach `status` to the thrown error, so UI error maps (400/404/409 branches) work.
 *   2. Self-heal a stale `extension_permissions` claim: on 403, re-mint the claim ONCE and
 *      replay the request ONCE. Only if that still fails does a PermissionDeniedError surface.
 *
 * See ADO #7699 — a permission granted after sign-in made the dashboard offer an action the
 * server always refused, and the generic "please try again" toast hid why.
 */
export const handleApiResponse = async (
  { request, response }: { request: Request; response: Response },
  { api, scopes }: { api: string; scopes: string[] }
): Promise<Response | undefined> => {
  if (response.ok) {
    return undefined;
  }

  if (response.status !== 403) {
    throw await asStatusError(response);
  }

  const { token, claimIsFresh } = await remintClaimOnce(scopes);

  if (!token) {
    throw denied(api, claimIsFresh, await readBody(response));
  }

  const retried = await replay(request, token);

  if (!retried) {
    // The claim IS fresh now, so a further attempt would use it — but this request cannot be
    // replayed. Only reachable if a client forgot to call rememberRequestBody.
    logger.error('Could not replay a 403 after re-minting the claim: body not captured', {
      api,
      method: request.method
    });
    throw denied(api, true, await readBody(response));
  }

  if (retried.ok) {
    return retried;
  }

  if (retried.status !== 403) {
    // The claim was the problem, but something else is wrong now — surface it honestly
    // rather than reporting a permission failure that no longer applies.
    throw await asStatusError(retried);
  }

  throw denied(api, true, await readBody(retried));
};
